import React, { useEffect, useRef, useState } from 'react';
import { Plus, X } from 'lucide-react';
import Button from '../../common/Button.js';
import ArticleTypeSelect from '../ArticleTypeSelect.js';
import { inputFilters } from '../../common/Validation.js';
import {
  emptyParcel,
  parcelFreightAmount,
  recomputeParcel,
  sumChargeableWeight,
  sumParcelFreightAmount,
  toNum,
  cloneParcelConfig,
  MAX_PACKAGE_CLONE_COUNT,
} from '../../../utils/cargoParcelUtils.js';

const compactInputClass =
  'w-full min-w-0 px-2 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs sm:text-sm disabled:bg-gray-50 disabled:text-gray-700';

const Field = ({ label, children, className = '', required = false }) => (
  <div className={`min-w-0 ${className}`}>
    <label className="block text-[11px] sm:text-xs font-medium text-gray-500 mb-1">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

/**
 * Multi-parcel dimension form for cargo shipments.
 * Packages are edited one at a time via tabs so extra parcels do not stack.
 */
const PackageDimensionForm = ({
  parcels = [],
  onChange,
  readOnly = false,
  startSrNo = 1,
  headerHint = '',
  title = '',
  badge = '',
  ratePerKg = 0,
}) => {
  const rows = parcels.length ? parcels : [emptyParcel(startSrNo)];
  const [activeIndex, setActiveIndex] = useState(0);
  const [multiplyCount, setMultiplyCount] = useState('');
  const [multiplyError, setMultiplyError] = useState('');
  const qtyInputRef = useRef(null);

  useEffect(() => {
    if (activeIndex > rows.length - 1) {
      setActiveIndex(Math.max(0, rows.length - 1));
    }
  }, [rows.length, activeIndex]);

  const safeIndex = Math.min(activeIndex, rows.length - 1);
  const row = rows[safeIndex] || emptyParcel(startSrNo);
  const computed = recomputeParcel(row);
  const canRemove = !readOnly && rows.length > 1;

  const updateRow = (index, patch) => {
    if (readOnly) return;
    const next = rows.map((item, i) => {
      if (i !== index) return item;
      return recomputeParcel({ ...item, ...patch });
    });
    onChange(next);
  };

  const addRow = () => {
    if (readOnly) return;
    const nextSr = startSrNo + rows.length;
    onChange([...rows.map(recomputeParcel), emptyParcel(nextSr)]);
    setActiveIndex(rows.length);
  };

  const cloneActiveRows = () => {
    if (readOnly) return;
    const count = Number.parseInt(String(multiplyCount).trim(), 10);
    if (!Number.isFinite(count) || count < 1) {
      setMultiplyError('Enter a valid count (1 or more)');
      return;
    }
    if (count > MAX_PACKAGE_CLONE_COUNT) {
      setMultiplyError(`Maximum ${MAX_PACKAGE_CLONE_COUNT} packages at once`);
      return;
    }
    if (rows.length + count > MAX_PACKAGE_CLONE_COUNT) {
      setMultiplyError(`Total packages cannot exceed ${MAX_PACKAGE_CLONE_COUNT}`);
      return;
    }
    setMultiplyError('');
    const source = rows[safeIndex] || rows[0];
    const clones = Array.from({ length: count }, (_, i) =>
      cloneParcelConfig(source, startSrNo + rows.length + i),
    );
    onChange([...rows.map(recomputeParcel), ...clones]);
    setActiveIndex(rows.length);
    setMultiplyCount('');
  };

  const removeRow = (index) => {
    if (!canRemove) return;
    const next = rows
      .filter((_, i) => i !== index)
      .map((item, i) => {
        const sr = startSrNo + i;
        return recomputeParcel({
          ...item,
          sr_no: sr,
          package_name: item.package_name || 'PACKAGE',
        });
      });
    onChange(next);
    setActiveIndex((prev) => {
      if (prev > index) return prev - 1;
      if (prev >= next.length) return Math.max(0, next.length - 1);
      return prev;
    });
  };

  const totalChargeable = sumChargeableWeight(rows);
  const packageAmount = parcelFreightAmount(row, ratePerKg);
  const totalPackageAmount = sumParcelFreightAmount(rows, ratePerKg);
  const tabLabel = (index) => {
    const item = recomputeParcel(rows[index] || {});
    const qty = item.qty || '0';
    const wt = Math.round(toNum(item.chargeable_weight));
    const amount = parcelFreightAmount(item, ratePerKg);
    return {
      title: `Package ${startSrNo + index}`,
      meta: `${qty} qty · ${wt} kg${amount > 0 ? ` · ₹${amount.toFixed(0)}` : ''}`,
    };
  };

  return (
    <div className="space-y-3 min-w-0">
      <div className="flex items-center gap-2 min-w-0">
        <div className="flex items-center gap-2 shrink-0">
          {title ? (
            <h3 className="text-sm sm:text-base font-semibold text-secondary whitespace-nowrap">{title}</h3>
          ) : null}
          {badge ? (
            <span className="text-[10px] sm:text-xs font-medium text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full whitespace-nowrap">
              {badge}
            </span>
          ) : null}
        </div>

        <div className="flex-1 min-w-0 overflow-x-auto">
          <div className="flex items-center gap-1.5 min-w-0">
            {rows.map((_, index) => {
              const { title: tabTitle, meta } = tabLabel(index);
              const isActive = index === safeIndex;
              return (
                <div
                  key={index}
                  className={`shrink-0 inline-flex items-center gap-0.5 rounded-lg border pl-2 pr-0.5 py-1 transition ${
                    isActive
                      ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
                      : 'bg-white border-gray-200 text-gray-600'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setActiveIndex(index)}
                    className="min-w-0 text-left"
                    title={`${tabTitle} · ${meta}`}
                  >
                    <span className="block text-[11px] sm:text-xs font-semibold leading-tight whitespace-nowrap">
                      {tabTitle}
                    </span>
                  </button>
                  {canRemove && (
                    <button
                      type="button"
                      onClick={() => removeRow(index)}
                      className={`p-1 rounded-md ${
                        isActive
                          ? 'text-white/90 hover:bg-blue-500'
                          : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                      }`}
                      title={`Remove ${tabTitle}`}
                      aria-label={`Remove ${tabTitle}`}
                    >
                      <X size={12} strokeWidth={2.5} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {!readOnly && (
          <div className="flex items-center gap-1.5 shrink-0">
            <div
              className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-1.5 py-0.5"
              title="Clone active package N times"
            >
              <span className="text-[10px] sm:text-xs font-medium text-gray-500 whitespace-nowrap">×</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={multiplyCount}
                onChange={(e) => {
                  setMultiplyCount(e.target.value.replace(/\D/g, ''));
                  setMultiplyError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    cloneActiveRows();
                  }
                }}
                placeholder="N"
                className="w-10 sm:w-12 min-w-0 px-1 py-1 text-xs sm:text-sm text-center border-0 focus:outline-none focus:ring-0 bg-transparent"
                aria-label="Number of packages to clone"
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={cloneActiveRows}
                title="Clone active package"
                className="!px-2 !py-1 !text-[11px] sm:!text-xs whitespace-nowrap"
              >
                Clone
              </Button>
            </div>
            <Button
              type="button"
              variant="primary"
              size="sm"
              icon={Plus}
              onClick={addRow}
              title="Add package"
              className="shrink-0"
            >
              Add
            </Button>
          </div>
        )}
      </div>

      {headerHint ? (
        <p className="text-xs sm:text-sm text-gray-500 leading-snug mb-0">{headerHint}</p>
      ) : null}

      {multiplyError ? (
        <p className="text-xs text-red-600 font-medium">{multiplyError}</p>
      ) : null}

      <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3 sm:p-4 min-w-0 relative">
        <div className="flex items-start justify-between gap-2 mb-3">
          <p className="text-xs sm:text-sm font-semibold text-secondary">
            Package #{startSrNo + safeIndex}
          </p>
          {canRemove && (
            <button
              type="button"
              onClick={() => removeRow(safeIndex)}
              className="p-1 -mt-1 -mr-1 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50"
              title="Remove this package"
              aria-label="Remove this package"
            >
              <X size={16} strokeWidth={2.5} />
            </button>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2 sm:gap-3 min-w-0">
          <Field label="Package" required>
            <ArticleTypeSelect
              value={row.package_name || 'PACKAGE'}
              readOnly={readOnly}
              placeholder="Select package"
              onEnterNext={() => qtyInputRef.current?.focus()}
              onChange={(next) =>
                updateRow(safeIndex, {
                  package_name: next,
                  article_type: next,
                })
              }
            />
          </Field>
          <Field label="Qty" required>
            <input
              ref={qtyInputRef}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              disabled={readOnly}
              value={row.qty}
              onChange={(e) =>
                updateRow(safeIndex, { qty: inputFilters.digitsOnly(e.target.value) })
              }
              className={compactInputClass}
            />
          </Field>
          <Field label="Actual Wt (kg)" required>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              disabled={readOnly}
              value={row.actual_weight}
              onChange={(e) =>
                updateRow(safeIndex, { actual_weight: inputFilters.digitsOnly(e.target.value) })
              }
              className={compactInputClass}
            />
          </Field>

          <Field label="L (ft)" required>
            <input
              type="text"
              inputMode="decimal"
              disabled={readOnly}
              value={row.length_cm}
              onChange={(e) =>
                updateRow(safeIndex, { length_cm: inputFilters.amount(e.target.value) })
              }
              className={compactInputClass}
            />
          </Field>
          <Field label="W (ft)" required>
            <input
              type="text"
              inputMode="decimal"
              disabled={readOnly}
              value={row.width_cm}
              onChange={(e) =>
                updateRow(safeIndex, { width_cm: inputFilters.amount(e.target.value) })
              }
              className={compactInputClass}
            />
          </Field>
          <Field label="H (ft)" required>
            <input
              type="text"
              inputMode="decimal"
              disabled={readOnly}
              value={row.height_cm}
              onChange={(e) =>
                updateRow(safeIndex, { height_cm: inputFilters.amount(e.target.value) })
              }
              className={compactInputClass}
            />
          </Field>

          <Field label="CBM">
            <div className={`${compactInputClass} bg-white text-gray-700`}>
              {toNum(computed.cbm).toFixed(3)}
            </div>
          </Field>
          <Field label="Vol Wt (kg)">
            <div className={`${compactInputClass} bg-white text-gray-700`}>
              {Math.round(toNum(computed.volumetric_weight))}
            </div>
          </Field>
          <Field label="Chargeable Wt (kg)" required>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              disabled={readOnly}
              value={computed.chargeable_weight}
              onChange={(e) =>
                updateRow(safeIndex, {
                  chargeable_weight: inputFilters.digitsOnly(e.target.value),
                  chargeable_manual: true,
                })
              }
              className={compactInputClass}
            />
          </Field>
          <Field label="Package Amount" className="col-span-3">
            <div className={`${compactInputClass} bg-white text-gray-800 font-semibold`}>
              ₹{packageAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              <span className="ml-2 font-normal text-gray-400">
                {toNum(ratePerKg) > 0
                  ? `(₹${toNum(ratePerKg)} / kg × ${Math.round(toNum(computed.chargeable_weight))} kg)`
                  : 'Enter Rate / KG in charges to see amount'}
              </span>
            </div>
          </Field>
        </div>

      </div>

      <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
        Total chargeable weight:{' '}
        <span className="font-semibold text-secondary">{Math.round(totalChargeable)} kg</span>
        <span className="mx-2 text-gray-300">·</span>
        Total package amount:{' '}
        <span className="font-semibold text-secondary">
          ₹{totalPackageAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
        </span>
        <span className="block sm:inline text-gray-400 sm:ml-2 mt-0.5 sm:mt-0">
          (chargeable = higher of actual vs volumetric; editable)
        </span>
      </p>
    </div>
  );
};

export default PackageDimensionForm;
